import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Search, X } from "lucide-react";

import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/utils/money";
import { fmtDate } from "@/utils/datetime";
import { TrashDeleteAction } from "@/components/trash/TrashDeleteAction";

type Account = { id: string; name: string; currency: string };
type Income = { id: string; source: string; description: string | null; income_date: string; amount_minor: number; currency: string; cash_account_id: string; cash_account_name: string | null };

const today = () => new Date().toISOString().slice(0, 10);

export default function OtherIncome() {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const canEdit = hasPerm("finance.create");
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [items, setItems] = useState<Income[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [f, setF] = useState({ date_from: "", date_to: "", cash_account_id: "", search: "" });
    const [editing, setEditing] = useState<Income | "new" | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
            const r = await api.get("/finance/other-income", { params });
            setItems(r.data.items || []); setTotal(r.data.total_minor || 0);
        } catch (e: any) { toast.error(formatApiError(e)); } finally { setLoading(false); }
    }, [f]);
    useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load]);
    useEffect(() => { api.get("/cash/accounts").then(r => setAccounts((r.data.items || []).filter((a: any) => a.is_active !== false))).catch(() => {}); }, []);
    const cur = items[0]?.currency || accounts[0]?.currency || "AZN";

    const input = "surface-input px-3 py-2 text-sm";
    return (
        <div className="space-y-5" data-testid="other-income-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-2xl font-semibold" style={{ color: "var(--text)" }}>{t("other_income.title")}</h1>
                {canEdit && (
                    <button data-testid="other-income-new-btn" onClick={() => setEditing("new")}
                        className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                        <Plus size={14} /> {t("other_income.new")}
                    </button>
                )}
            </div>
            <div className="surface-card p-4 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="other-income-filters">
                <div className="relative md:col-span-2">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                    <input data-testid="other-income-search" value={f.search} onChange={e => setF({ ...f, search: e.target.value })}
                        placeholder={t("other_income.search_placeholder")} className={`${input} w-full pl-9`} />
                </div>
                <input type="date" data-testid="other-income-from" value={f.date_from} onChange={e => setF({ ...f, date_from: e.target.value })} className={input} aria-label={t("other_income.date_from")} title={t("other_income.date_from")} />
                <input type="date" data-testid="other-income-to" value={f.date_to} onChange={e => setF({ ...f, date_to: e.target.value })} className={input} aria-label={t("other_income.date_to")} title={t("other_income.date_to")} />
                <select data-testid="other-income-account-filter" value={f.cash_account_id} onChange={e => setF({ ...f, cash_account_id: e.target.value })} className={input}>
                    <option value="">{t("other_income.all_accounts")}</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
            </div>
            <div className="surface-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="other-income-table">
                        <thead><tr className="text-left" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                            <th className="px-4 py-2 font-medium">{t("other_income.date")}</th>
                            <th className="px-4 py-2 font-medium">{t("other_income.source")}</th>
                            <th className="px-4 py-2 font-medium">{t("other_income.description")}</th>
                            <th className="px-4 py-2 font-medium">{t("other_income.account")}</th>
                            <th className="px-4 py-2 font-medium text-right">{t("other_income.amount")}</th>
                            {canEdit && <th className="px-4 py-2" />}
                        </tr></thead>
                        <tbody>
                            {loading && <tr><td colSpan={6} className="p-6 text-center"><Loader2 className="animate-spin inline" size={18} /></td></tr>}
                            {!loading && items.length === 0 && <tr><td colSpan={6} className="p-8 text-center" style={{ color: "var(--text-muted)" }} data-testid="other-income-empty">{t("other_income.empty")}</td></tr>}
                            {!loading && items.map(it => (
                                <tr key={it.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`other-income-row-${it.id}`}>
                                    <td className="px-4 py-2 whitespace-nowrap" style={{ color: "var(--text-muted)" }}>{fmtDate(it.income_date)}</td>
                                    <td className="px-4 py-2 font-medium" style={{ color: "var(--text)" }}>{it.source}</td>
                                    <td className="px-4 py-2 max-w-[320px] truncate" style={{ color: "var(--text-muted)" }} title={it.description || ""}>{it.description || "—"}</td>
                                    <td className="px-4 py-2" style={{ color: "var(--text)" }}>{it.cash_account_name || "—"}</td>
                                    <td className="px-4 py-2 text-right font-mono tabular-nums" style={{ color: "var(--success-fg)" }}>{fmtMoney(it.amount_minor, it.currency)}</td>
                                    {canEdit && (
                                        <td className="px-4 py-2">
                                            <div className="flex justify-end items-center gap-1">
                                                <button data-testid={`other-income-edit-${it.id}`} onClick={() => setEditing(it)} title={t("common.edit")}
                                                    className="p-1.5 rounded-md hover:bg-[var(--input)]" style={{ color: "var(--text-muted)" }}><Pencil size={15} /></button>
                                                <TrashDeleteAction entityType="other_income" entityId={it.id} entityLabel={it.source} onArchived={load} />
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot><tr className="border-t" style={{ borderColor: "var(--border)" }}>
                            <td colSpan={4} className="px-4 py-3 font-medium" style={{ color: "var(--text)" }}>{t("other_income.total")}</td>
                            <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums" style={{ color: "var(--text)" }} data-testid="other-income-total">{fmtMoney(total, cur)}</td>
                            {canEdit && <td />}
                        </tr></tfoot>
                    </table>
                </div>
            </div>
            {editing && <IncomeDialog accounts={accounts} income={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
}

function IncomeDialog({ accounts, income, onClose, onSaved }: { accounts: Account[]; income: Income | null; onClose: () => void; onSaved: () => void }) {
    const { t } = useTranslation();
    const [s, setS] = useState({
        source: income?.source || "", description: income?.description || "",
        income_date: income?.income_date || today(), amount: income ? String(income.amount_minor / 100) : "",
        cash_account_id: income?.cash_account_id || "",
    });
    const [errs, setErrs] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    const validate = () => {
        const e: Record<string, string> = {};
        if (!s.source.trim()) e.source = t("other_income.err.source");
        if (!(parseFloat(s.amount) > 0)) e.amount = t("other_income.err.amount");
        if (!s.cash_account_id) e.cash_account_id = t("other_income.err.account");
        if (!s.income_date) e.income_date = t("other_income.err.date");
        if (s.description.length > 500) e.description = t("other_income.err.description");
        setErrs(e);
        return Object.keys(e).length === 0;
    };
    const save = async () => {
        if (!validate()) return;
        setSaving(true);
        const body = { source: s.source.trim(), description: s.description.trim() || null, income_date: s.income_date,
            amount_minor: Math.round(parseFloat(s.amount) * 100), cash_account_id: s.cash_account_id };
        try {
            if (income) await api.patch(`/finance/other-income/${income.id}`, body);
            else await api.post("/finance/other-income", body);
            toast.success(t(income ? "other_income.updated" : "other_income.created"));
            onSaved();
        } catch (e: any) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    };
    const field = (key: string, label: string, el: React.ReactNode) => (
        <label className="block">
            <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>
            {el}
            {errs[key] && <div className="text-xs mt-1" style={{ color: "var(--error-fg)" }} data-testid={`other-income-err-${key}`}>{errs[key]}</div>}
        </label>
    );
    const cls = "surface-input w-full px-3 py-2 text-sm";
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => !saving && onClose()} data-testid="other-income-dialog">
            <div className="surface-card w-full max-w-md p-6 space-y-3" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-1">
                    <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t(income ? "other_income.edit" : "other_income.new")}</h2>
                    <button onClick={onClose} style={{ color: "var(--text-muted)" }} data-testid="other-income-dialog-close"><X size={18} /></button>
                </div>
                {field("source", t("other_income.source"), <input data-testid="other-income-source" value={s.source} maxLength={255} onChange={e => setS({ ...s, source: e.target.value })} className={cls} />)}
                {field("description", t("other_income.description"), <textarea data-testid="other-income-description" value={s.description} maxLength={500} rows={3} onChange={e => setS({ ...s, description: e.target.value })} className={cls} />)}
                {field("income_date", t("other_income.date"), <input type="date" data-testid="other-income-date" value={s.income_date} onChange={e => setS({ ...s, income_date: e.target.value })} className={cls} />)}
                {field("amount", t("other_income.amount"), <input type="number" step="0.01" min="0" data-testid="other-income-amount" value={s.amount} onChange={e => setS({ ...s, amount: e.target.value })} className={cls} />)}
                {field("cash_account_id", t("other_income.account"), (
                    <select data-testid="other-income-account" value={s.cash_account_id} onChange={e => setS({ ...s, cash_account_id: e.target.value })} className={cls}>
                        <option value="">{t("other_income.pick_account")}</option>
                        {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                ))}
                <button data-testid="other-income-save" disabled={saving} onClick={save}
                    className="w-full inline-flex justify-center items-center gap-2 rounded-md py-2 text-sm font-medium disabled:opacity-60"
                    style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>
                    {saving && <Loader2 className="animate-spin" size={16} />} {t("common.save")}
                </button>
            </div>
        </div>
    );
}
